import React, { FC } from 'react';
import { Button, Form, Input, Layout, message, Select } from 'antd';
import { useMutation, useQueryCache } from 'react-query';
import { useHistory } from 'react-router';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { QueryKey } from 'api/queryKey';
import { Content } from 'common/StyledComponents';
import { useRole } from 'common/hooks/useRole';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import {
  defaultFormLayout,
  defaultFormTailLayout,
} from 'common/config/formLayout';
import { AdditionalDevicesManagementRoute } from 'routes';

interface NewAdditionalDeviceFormValues {
  serialNumber: string;
  deviceType: 'OXIMETER' | 'BP_MONITOR';
}

export const NewAdditionalDevicePage: FC = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const queryCache = useQueryCache();
  const intlMessage = useFormatMessage();
  const { isSuperAdmin } = useRole();

  const [createAdditionalDevice, { isLoading }] = useMutation(
    BusinessPartnersAPI.createAdditionalDevice
  );

  const handleSubmit = (values: NewAdditionalDeviceFormValues) => {
    if (!isSuperAdmin) {
      return;
    }

    createAdditionalDevice(values, {
      onSuccess: () => {
        queryCache.invalidateQueries(QueryKey.SuperAdminAdditionalDevicesList);
        message.success(
          intlMessage(
            { id: 'devices.addNew.success' },
            { serialNumber: values.serialNumber }
          )
        );
        history.push(AdditionalDevicesManagementRoute);
      },
    });
  };

  return (
    <Layout>
      <Content>
        <Form
          form={form}
          {...defaultFormLayout}
          onFinish={handleSubmit}
          style={{ maxWidth: 650, marginTop: 24 }}
        >
          <Form.Item
            label={intlMessage({ id: 'deviceSerialNumber' })}
            name="serialNumber"
            rules={[
              {
                required: true,
                message: intlMessage(
                  { id: 'validation.required' },
                  { field: intlMessage({ id: 'deviceSerialNumber' }) }
                ),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={intlMessage({ id: 'deviceType' })}
            name="deviceType"
            rules={[
              {
                required: true,
                message: intlMessage(
                  { id: 'validation.required' },
                  { field: intlMessage({ id: 'deviceType' }) }
                ),
              },
            ]}
          >
            <Select>
              <Select.Option value="OXIMETER">OXY</Select.Option>
              <Select.Option value="BP_MONITOR">BPM</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item {...defaultFormTailLayout}>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {intlMessage({ id: 'save' })}
            </Button>
          </Form.Item>
        </Form>
      </Content>
    </Layout>
  );
};
